import { supabase } from '../lib/supabase'
import {
  ShopBroker,
  ShopCategory,
  ShopParameter,
  ShopProduct,
  ShopProductImage,
  ShopOrder,
  ShopOrderItem,
  ShopPointsHistory,
  ShopOrderStatus,
  ShopOrderUpdateData,
  ShopStockMovement,
  ShopStockMovementType,
  ShopStockMovementFilters,
  ShopAccessLog,
  ShopHistoricoAcesso,
  ShopTopAcessoEntry,
} from '../types/shop'

type ShopBrokerLookup = { id: string; name: string; id_scgp: number | null }

/** IDs em `brokers` que pertencem ao shop e batem com SCGP/nome; null = sem filtro; [] = nenhum match */
async function resolveBrokerIdsInShopScope(filters?: {
  scgpId?: string
  brokerName?: string
}): Promise<string[] | null> {
  const scgpTrim = filters?.scgpId?.trim()
  const nameTrim = filters?.brokerName?.trim()
  if (!scgpTrim && !nameTrim) return null

  const { data: shopBrokers, error: sbError } = await supabase
    .from('shop_brokers')
    .select('broker_id')

  if (sbError) throw sbError

  const shopBrokerIds = Array.from(
    new Set(
      (shopBrokers || [])
        .map((sb: { broker_id: string | null }) => sb.broker_id)
        .filter((id): id is string => Boolean(id))
    )
  )
  if (shopBrokerIds.length === 0) return []

  const { data: brokersData, error: brokersError } = await supabase
    .from('brokers')
    .select('id, name, id_scgp')
    .in('id', shopBrokerIds)

  if (brokersError) throw brokersError
  if (!brokersData?.length) return []

  let matched = brokersData as ShopBrokerLookup[]

  if (scgpTrim) {
    if (/^\d+$/.test(scgpTrim)) {
      matched = matched.filter(
        (b) => b.id_scgp != null && String(b.id_scgp).includes(scgpTrim)
      )
    } else {
      const q = scgpTrim.toLowerCase()
      matched = matched.filter((b) => (b.name || '').toLowerCase().includes(q))
    }
  }

  if (nameTrim) {
    const q = nameTrim.toLowerCase()
    matched = matched.filter((b) => (b.name || '').toLowerCase().includes(q))
  }

  return matched.map((b) => b.id)
}

/** Corretores (shop_broker_id) que batem com os filtros SCGP/nome; null = não filtrar; Set vazio = nenhum match */
async function resolveShopBrokerIdsForIndicators(
  scgpId?: string,
  brokerName?: string,
): Promise<Set<string> | null> {
  const brokerIds = await resolveBrokerIdsInShopScope({ scgpId, brokerName })
  if (brokerIds === null) return null
  if (brokerIds.length === 0) return new Set<string>()

  const { data: shopBrokers, error: sbError } = await supabase
    .from('shop_brokers')
    .select('id')
    .in('broker_id', brokerIds)

  if (sbError) throw sbError
  return new Set((shopBrokers || []).map((sb: { id: string }) => sb.id))
}

function indicatorDateRange(months: number, dateFrom?: string, dateTo?: string): { startISO: string; endISO: string } {
  const endDate = dateTo ? new Date(dateTo) : new Date()
  const startDate = dateFrom
    ? new Date(dateFrom)
    : (() => {
        const s = new Date(endDate)
        s.setMonth(s.getMonth() - months)
        return s
      })()
  return {
    startISO: startDate.toISOString(),
    endISO: endDate.toISOString(),
  }
}

export const shopService = {
  // ============================================
  // CATEGORIAS
  // ============================================
  async listCategories(): Promise<ShopCategory[]> {
    const { data, error } = await supabase
      .from('shop_categories')
      .select('*')
      .order('display_order', { ascending: true })
      .order('name', { ascending: true })

    if (error) throw error
    return (data || []) as ShopCategory[]
  },

  async getCategory(id: string): Promise<ShopCategory | null> {
    const { data, error } = await supabase
      .from('shop_categories')
      .select('*')
      .eq('id', id)
      .single()

    if (error) throw error
    return data as ShopCategory
  },

  async upsertCategory(category: Partial<ShopCategory>): Promise<ShopCategory | null> {
    const { data, error } = await supabase
      .from('shop_categories')
      .upsert(category, { onConflict: 'id' })
      .select('*')
      .single()

    if (error) throw error
    return data as ShopCategory
  },

  async deleteCategory(id: string): Promise<void> {
    const { error } = await supabase
      .from('shop_categories')
      .delete()
      .eq('id', id)

    if (error) throw error
  },

  // ============================================
  // PARÂMETROS DO SHOP
  // ============================================
  async listParameters(): Promise<ShopParameter[]> {
    const { data, error } = await supabase
      .from('shop_parameters')
      .select('*')
      .order('key', { ascending: true })

    if (error) throw error
    return (data || []) as ShopParameter[]
  },

  async updateParameter(
    id: string,
    data: { value: string; description?: string }
  ): Promise<ShopParameter | null> {
    const payload: { value: string; description?: string } = { value: data.value }
    if (data.description !== undefined) payload.description = data.description

    const { data: result, error } = await supabase
      .from('shop_parameters')
      .update(payload)
      .eq('id', id)
      .select()
      .single()

    if (error) throw error
    return result as ShopParameter
  },

  // ============================================
  // HISTÓRICO DE ACESSOS DO SHOP
  // ============================================

  async getAccessLogs(filters?: {
    brokerId?: string
    dateFrom?: string
    dateTo?: string
  }): Promise<ShopAccessLog[]> {
    let query = supabase
      .from('shop_access_logs')
      .select('*')
      .order('accessed_at', { ascending: false })

    if (filters?.brokerId) {
      query = query.eq('broker_id', filters.brokerId)
    }

    if (filters?.dateFrom) {
      query = query.gte('accessed_at', filters.dateFrom)
    }

    if (filters?.dateTo) {
      query = query.lte('accessed_at', filters.dateTo)
    }

    const { data, error } = await query

    if (error) {
      console.error('Erro ao buscar logs de acesso do shop:', error)
      throw error
    }

    return (data || []) as ShopAccessLog[]
  },

  async getHistoricoAcessos(filters?: {
    corretorQuery?: string
    cnpjQuery?: string
    dateFrom?: string
    dateTo?: string
    brokerId?: string
  }): Promise<ShopHistoricoAcesso[]> {
    const logsData = await this.getAccessLogs({
      dateFrom: filters?.dateFrom,
      dateTo: filters?.dateTo,
      brokerId: filters?.brokerId,
    })

    const brokerIds = Array.from(new Set(logsData.map(l => l.broker_id).filter(Boolean) as string[]))

    const brokersMap = new Map<string, { name: string; cnpj?: string }>()
    if (brokerIds.length > 0) {
      try {
        const { data: brokers, error } = await supabase
          .from('brokers')
          .select('*')
          .in('id', brokerIds)

        if (error) {
          console.error('Erro ao buscar corretores para histórico do shop:', error)
        } else if (brokers && brokers.length > 0) {
          brokers.forEach((b: any) => {
            const brokerName = b.name || b.nome || b.full_name || b.broker_name || b.company_name || b.id || '(Sem nome)'
            const brokerCnpj = b.cnpj || b.cpf || ''
            brokersMap.set(b.id, {
              name: brokerName,
              cnpj: brokerCnpj,
            })
          })
        }
      } catch (error) {
        console.error('Erro ao buscar corretores (shop histórico):', error)
      }
    }

    let historico: ShopHistoricoAcesso[] = logsData.map(log => {
      const info = log.broker_id ? brokersMap.get(log.broker_id) : null
      const corretor = info?.name || (log.broker_id ? `ID: ${log.broker_id}` : '')
      const cnpj = info?.cnpj || ''

      return {
        id: log.id,
        corretor,
        cnpj,
        primeiroAcesso: log.accessed_at,
        ultimoAcesso: log.accessed_at,
        broker_id: log.broker_id,
        ip_address: log.ip_address,
        user_agent: log.user_agent,
        token: log.token,
        accessed_at: log.accessed_at,
      }
    })

    if (filters?.corretorQuery) {
      const query = filters.corretorQuery.toLowerCase()
      historico = historico.filter(h => h.corretor.toLowerCase().includes(query))
    }

    if (filters?.cnpjQuery) {
      const query = filters.cnpjQuery.toLowerCase()
      historico = historico.filter(h => h.cnpj.toLowerCase().includes(query))
    }

    return historico.sort(
      (a, b) =>
        new Date(b.ultimoAcesso).getTime() - new Date(a.ultimoAcesso).getTime(),
    )
  },

  async getTotalAcessos(): Promise<number> {
    const { count, error } = await supabase
      .from('shop_access_logs')
      .select('*', { count: 'exact', head: true })

    if (error) {
      console.error('Erro ao contar acessos do shop:', error)
      throw error
    }
    return count || 0
  },

  async getAcessosHoje(): Promise<number> {
    const hoje = new Date()
    hoje.setHours(0, 0, 0, 0)
    const hojeStr = hoje.toISOString()

    const amanha = new Date(hoje)
    amanha.setDate(amanha.getDate() + 1)
    const amanhaStr = amanha.toISOString()

    const { count, error } = await supabase
      .from('shop_access_logs')
      .select('*', { count: 'exact', head: true })
      .gte('accessed_at', hojeStr)
      .lt('accessed_at', amanhaStr)

    if (error) {
      console.error('Erro ao contar acessos de hoje do shop:', error)
      throw error
    }
    return count || 0
  },

  /** Corretores distintos e top N por volume de acessos em shop_access_logs. */
  async getBrokerAccessStats(topN: number = 3): Promise<{
    distinctCorretores: number
    topCorretores: ShopTopAcessoEntry[]
  }> {
    const PAGE_SIZE = 1000
    let from = 0
    const counts = new Map<string, number>()

    while (true) {
      const to = from + PAGE_SIZE - 1
      const { data, error } = await supabase
        .from('shop_access_logs')
        .select('broker_id')
        .not('broker_id', 'is', null)
        .order('id', { ascending: true })
        .range(from, to)

      if (error) {
        console.error('Erro ao buscar estatísticas de acesso (shop):', error)
        throw error
      }

      const rows = (data || []) as Array<{ broker_id: string | null }>
      for (const r of rows) {
        if (!r.broker_id) continue
        counts.set(r.broker_id, (counts.get(r.broker_id) ?? 0) + 1)
      }

      if (rows.length < PAGE_SIZE) break
      from += PAGE_SIZE
    }

    const distinctCorretores = counts.size
    if (distinctCorretores === 0) {
      return { distinctCorretores: 0, topCorretores: [] }
    }

    const sorted = Array.from(counts.entries()).sort((a, b) => b[1] - a[1])
    const topPairs = topN > 0 ? sorted.slice(0, topN) : []

    if (topPairs.length === 0) {
      return { distinctCorretores, topCorretores: [] }
    }

    const topIds = topPairs.map(([bid]) => bid)
    const { data: brokers, error: brokersError } = await supabase
      .from('brokers')
      .select('id, name, id_scgp')
      .in('id', topIds)

    if (brokersError) throw brokersError

    const brokerMap = new Map<string, { name: string; id_scgp: number | null }>()
    for (const b of brokers || []) {
      const row = b as { id: string; name?: string; id_scgp?: number | string | null }
      const rawScgp = row.id_scgp
      const idScgp = rawScgp == null || rawScgp === '' ? null : Number(rawScgp)
      brokerMap.set(row.id, {
        name: row.name?.trim() || `ID: ${row.id}`,
        id_scgp: Number.isFinite(idScgp) ? idScgp : null,
      })
    }

    const topCorretores: ShopTopAcessoEntry[] = topPairs.map(([broker_id, acessos]) => {
      const info = brokerMap.get(broker_id)
      return {
        broker_id,
        nome: info?.name ?? `ID: ${broker_id}`,
        id_scgp: info?.id_scgp ?? null,
        acessos,
      }
    })

    return { distinctCorretores, topCorretores }
  },

  /** Corretores distintos que já possuem pelo menos um registro em shop_access_logs (broker_id não nulo). */
  async getDistinctCorretoresComAcesso(): Promise<number> {
    const { distinctCorretores } = await this.getBrokerAccessStats(0)
    return distinctCorretores
  },

  // ============================================
  // PRODUTOS
  // ============================================
  async listProducts(filters?: {
    categoryId?: string
    isActive?: boolean
    isFeatured?: boolean
  }): Promise<ShopProduct[]> {
    let query = supabase
      .from('shop_products')
      .select(`
        *,
        category:shop_categories(*),
        images:shop_product_images(*)
      `)
      .order('is_featured', { ascending: false })
      .order('created_at', { ascending: false })

    if (filters?.categoryId) {
      query = query.eq('category_id', filters.categoryId)
    }

    if (filters?.isActive !== undefined) {
      query = query.eq('is_active', filters.isActive)
    }

    if (filters?.isFeatured !== undefined) {
      query = query.eq('is_featured', filters.isFeatured)
    }

    const { data, error } = await query

    if (error) throw error
    return (data || []) as ShopProduct[]
  },

  async listAvailableProducts(): Promise<ShopProduct[]> {
    const { data, error } = await supabase
      .from('v_shop_available_products')
      .select('*')
      .order('is_featured', { ascending: false })
      .order('created_at', { ascending: false })

    if (error) throw error
    return (data || []) as ShopProduct[]
  },

  async getProduct(id: string): Promise<ShopProduct | null> {
    const { data, error } = await supabase
      .from('shop_products')
      .select(`
        *,
        category:shop_categories(*),
        images:shop_product_images(*)
      `)
      .eq('id', id)
      .single()

    if (error) throw error
    return data as ShopProduct
  },

  async upsertProduct(product: Partial<ShopProduct>): Promise<ShopProduct | null> {
    const { data, error } = await supabase
      .from('shop_products')
      .upsert(product, { onConflict: 'id' })
      .select('*')
      .single()

    if (error) throw error
    return data as ShopProduct
  },

  async deleteProduct(id: string): Promise<void> {
    const { error } = await supabase
      .from('shop_products')
      .delete()
      .eq('id', id)

    if (error) throw error
  },

  // ============================================
  // ESTOQUE - MOVIMENTAÇÕES
  // ============================================
  async listStockMovements(filters?: ShopStockMovementFilters): Promise<ShopStockMovement[]> {
    let query = supabase
      .from('shop_stock_movements')
      .select(`
        *,
        product:shop_products(name),
        created_by_user:profiles!shop_stock_movements_created_by_fkey(name)
      `)
      .order('created_at', { ascending: false })
      .limit(200)

    if (filters?.productId) {
      query = query.eq('product_id', filters.productId)
    }
    if (filters?.movementType) {
      query = query.eq('movement_type', filters.movementType)
    }
    if (filters?.dateFrom) {
      query = query.gte('created_at', filters.dateFrom)
    }
    if (filters?.dateTo) {
      query = query.lte('created_at', filters.dateTo)
    }

    const { data, error } = await query
    if (error) throw error
    return (data || []) as ShopStockMovement[]
  },

  /**
   * Registra uma movimentação de estoque e atualiza shop_products.stock_quantity.
   * - in: soma quantity ao estoque atual.
   * - out: subtrai quantity (estoque não pode ficar negativo).
   * - adjustment: quantity é o novo valor absoluto do estoque (quantity_after = quantity).
   * Para movimentações manuais use reference_type 'manual' e reference_id null.
   */
  async registerStockMovement(params: {
    product_id: string
    movement_type: ShopStockMovementType
    quantity: number
    description: string
    created_by?: string | null
    reference_type?: ShopStockMovement['reference_type']
    reference_id?: string | null
  }): Promise<ShopStockMovement> {
    const { product_id, movement_type, quantity, description, created_by = null, reference_type = 'manual', reference_id = null } = params
    if (quantity <= 0) throw new Error('Quantidade deve ser maior que zero.')
    if (!description || !description.trim()) throw new Error('Descrição é obrigatória.')

    const product = await this.getProduct(product_id)
    if (!product) throw new Error('Produto não encontrado.')
    const quantity_before = product.stock_quantity ?? 0

    let quantity_after: number
    let movementQuantity: number // sempre positivo para a tabela

    if (movement_type === 'in') {
      quantity_after = quantity_before + quantity
      movementQuantity = quantity
    } else if (movement_type === 'out') {
      if (quantity_before < quantity) {
        throw new Error(`Estoque insuficiente. Disponível: ${quantity_before}.`)
      }
      quantity_after = quantity_before - quantity
      movementQuantity = quantity
    } else {
      // adjustment: quantity é o novo valor absoluto do estoque
      quantity_after = quantity
      movementQuantity = Math.abs(quantity_after - quantity_before)
    }

    const { data: movement, error: insertError } = await supabase
      .from('shop_stock_movements')
      .insert({
        product_id,
        movement_type,
        quantity: movementQuantity,
        quantity_before,
        quantity_after,
        reference_type,
        reference_id,
        description: description.trim(),
        created_by: created_by || null,
      })
      .select('*')
      .single()

    if (insertError) throw insertError

    const { error: updateError } = await supabase
      .from('shop_products')
      .update({ stock_quantity: quantity_after })
      .eq('id', product_id)

    if (updateError) throw updateError

    return movement as ShopStockMovement
  },

  // ============================================
  // IMAGENS DE PRODUTOS
  // ============================================
  async listProductImages(productId: string): Promise<ShopProductImage[]> {
    const { data, error } = await supabase
      .from('shop_product_images')
      .select('*')
      .eq('product_id', productId)
      .order('display_order', { ascending: true })

    if (error) throw error
    return (data || []) as ShopProductImage[]
  },

  async upsertProductImage(image: Partial<ShopProductImage>): Promise<ShopProductImage | null> {
    // Se temos um ID, fazer update ao invés de upsert para preservar campos existentes
    if (image.id) {
      const { data, error } = await supabase
        .from('shop_product_images')
        .update(image)
        .eq('id', image.id)
        .select('*')
        .single()

      if (error) throw error
      return data as ShopProductImage
    } else {
      // Se não temos ID, fazer insert
      const { data, error } = await supabase
        .from('shop_product_images')
        .insert(image)
        .select('*')
        .single()

      if (error) throw error
      return data as ShopProductImage
    }
  },

  async deleteProductImage(id: string): Promise<void> {
    const { error } = await supabase
      .from('shop_product_images')
      .delete()
      .eq('id', id)

    if (error) throw error
  },

  async uploadProductImage(file: File, productId: string): Promise<string> {
    const timestamp = Date.now()
    const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_')
    const fileName = `${productId}/${timestamp}_${sanitizedFileName}`

    const { data, error } = await supabase.storage
      .from('shop_corretor')
      .upload(fileName, file, {
        cacheControl: '3600',
        upsert: false,
        contentType: file.type
      })

    if (error) {
      console.error('Erro no upload da imagem:', error)
      throw new Error(`Falha ao enviar imagem: ${error.message}`)
    }

    // Como o bucket é privado, retornar apenas o path
    // A URL será gerada via createSignedUrl quando necessário
    return fileName
  },

  async getProductImageUrl(imagePath: string, expiresInSeconds = 3600): Promise<string | null> {
    if (!imagePath) return null

    const { data, error } = await supabase.storage
      .from('shop_corretor')
      .createSignedUrl(imagePath, expiresInSeconds)

    if (error) {
      console.error('Erro ao gerar URL assinada da imagem:', error.message)
      return null
    }

    return data?.signedUrl ?? null
  },

  async deleteProductImageFile(imagePath: string): Promise<void> {
    // imagePath pode ser uma URL ou apenas o path
    // Se for URL, extrair o path; se já for path, usar diretamente
    let filePath = imagePath

    // Se contém '/shop_corretor/', é uma URL - extrair o path
    if (imagePath.includes('/shop_corretor/')) {
      const urlParts = imagePath.split('/shop_corretor/')
      if (urlParts.length >= 2) {
        filePath = urlParts[1].split('?')[0] // Remover query params se houver
      } else {
        throw new Error('URL de imagem inválida')
      }
    }
    // Se contém 'storage/v1/object/sign/', é uma signed URL - extrair o path
    else if (imagePath.includes('storage/v1/object/sign/shop_corretor/')) {
      const urlParts = imagePath.split('storage/v1/object/sign/shop_corretor/')
      if (urlParts.length >= 2) {
        filePath = urlParts[1].split('?')[0]
      } else {
        throw new Error('URL assinada inválida')
      }
    }
    // Também verificar formato antigo '/shop/' para compatibilidade
    else if (imagePath.includes('/shop/')) {
      const urlParts = imagePath.split('/shop/')
      if (urlParts.length >= 2) {
        filePath = urlParts[1].split('?')[0]
      } else {
        throw new Error('URL de imagem inválida')
      }
    }
    // Caso contrário, assumir que já é o path

    const { error } = await supabase.storage
      .from('shop_corretor')
      .remove([filePath])

    if (error) {
      console.error('Erro ao remover imagem:', error)
      throw new Error(`Falha ao remover imagem: ${error.message}`)
    }
  },

  // ============================================
  // CORRETORES
  // ============================================
  /** Lista corretores da aba Corretores do Shop. Retorna APENAS corretores presentes em shop_brokers. */
  async listBrokers(filters?: {
    brokerId?: string
    scgpId?: string
    brokerName?: string
  }): Promise<ShopBroker[]> {
    let query = supabase
      .from('shop_brokers')
      .select('*')
      .order('created_at', { ascending: false })

    if (filters?.brokerId) {
      query = query.ilike('broker_id', `%${filters.brokerId}%`)
    }

    const brokerIdsFilter = await resolveBrokerIdsInShopScope(filters)
    if (brokerIdsFilter !== null) {
      if (brokerIdsFilter.length === 0) return []
      query = query.in('broker_id', brokerIdsFilter)
    }

    const { data, error } = await query

    if (error) throw error

    const shopBrokers = (data || []) as ShopBroker[]

    // Buscar nomes dos brokers separadamente
    const brokerIdsToFetch = Array.from(new Set(shopBrokers.map(sb => sb.broker_id).filter(Boolean)))
    if (brokerIdsToFetch.length > 0) {
      const { data: brokers, error: brokersError } = await supabase
        .from('brokers')
        .select('id, name, id_scgp')
        .in('id', brokerIdsToFetch)

      if (!brokersError && brokers) {
        const brokersMap = new Map<string, { name: string; id_scgp: number | null }>()
        brokers.forEach((b: any) => {
          brokersMap.set(b.id, { name: b.name || '', id_scgp: b.id_scgp })
        })

        return shopBrokers.map(sb => ({
          ...sb,
          broker: sb.broker_id && brokersMap.has(sb.broker_id)
            ? { name: brokersMap.get(sb.broker_id)!.name, id_scgp: brokersMap.get(sb.broker_id)!.id_scgp }
            : null
        }))
      }
    }

    return shopBrokers.map(sb => ({
      ...sb,
      broker: null
    }))
  },

  async getBroker(brokerId: string): Promise<ShopBroker | null> {
    const { data, error } = await supabase
      .from('shop_brokers')
      .select(`
        *,
        broker:brokers(name)
      `)
      .eq('broker_id', brokerId)
      .single()

    if (error) {
      if (error.code === 'PGRST116') {
        return null // Não encontrado
      }
      throw error
    }
    return data as ShopBroker
  },

  async getBrokerPoints(brokerId: string): Promise<ShopBroker | null> {
    return this.getBroker(brokerId)
  },

  /** Listar itens favoritados de um corretor (shop_broker_id = id do registro em shop_brokers) */
  async getBrokerFavoriteProducts(shopBrokerId: string): Promise<Array<{ product_id: string; product_name: string }>> {
    const { data, error } = await supabase
      .from('shop_favorite_products')
      .select(`
        product_id,
        product:shop_products(name)
      `)
      .eq('shop_broker_id', shopBrokerId)

    if (error) throw error

    return (data || []).map((item: any) => ({
      product_id: item.product_id,
      product_name: item.product?.name ?? 'Produto'
    }))
  },

  // ============================================
  // PEDIDOS
  // ============================================
  async listOrders(filters?: {
    brokerId?: string
    scgpId?: string
    brokerName?: string
    status?: ShopOrderStatus
    dateFrom?: string
    dateTo?: string
  }): Promise<ShopOrder[]> {
    let query = supabase
      .from('shop_orders')
      .select(`
        *,
        items:shop_order_items(
          *,
          product:shop_products(*)
        )
      `)
      .order('created_at', { ascending: false })

    if (filters?.brokerId) {
      // Filtrar por shop_broker_id ou buscar através de shop_brokers.broker_id
      query = query.ilike('shop_broker_id', `%${filters.brokerId}%`)
    }

    if (filters?.status) {
      query = query.eq('status', filters.status)
    }

    if (filters?.dateFrom) {
      query = query.gte('created_at', filters.dateFrom)
    }

    if (filters?.dateTo) {
      query = query.lte('created_at', filters.dateTo)
    }

    const { data, error } = await query

    if (error) {
      console.error('Erro ao buscar pedidos:', error)
      throw error
    }

    let orders = (data || []) as ShopOrder[]

    // Buscar nomes dos brokers através do relacionamento correto
    // shop_orders.shop_broker_id -> shop_brokers.id -> shop_brokers.broker_id -> brokers.id -> brokers.name
    const shopBrokerIds = Array.from(new Set(orders.map(o => o.shop_broker_id).filter(Boolean)))
    if (shopBrokerIds.length > 0) {
      // Primeiro buscar shop_brokers para obter broker_id
      const { data: shopBrokers, error: shopBrokersError } = await supabase
        .from('shop_brokers')
        .select('id, broker_id')
        .in('id', shopBrokerIds)

      if (!shopBrokersError && shopBrokers) {
        const shopBrokerToBrokerMap = new Map<string, string>()
        shopBrokers.forEach((sb: any) => {
          shopBrokerToBrokerMap.set(sb.id, sb.broker_id)
        })

        // Agora buscar nomes dos brokers
        const brokerIds = Array.from(new Set(Array.from(shopBrokerToBrokerMap.values()).filter(Boolean)))
        if (brokerIds.length > 0) {
          let brokersQuery = supabase
            .from('brokers')
            .select('id, name, id_scgp')
            .in('id', brokerIds)

          // Aplicar filtros por ID SCGP e nome do corretor
          if (filters?.scgpId && filters.scgpId.trim() !== '') {
            const scgpIdNum = parseInt(filters.scgpId)
            if (!isNaN(scgpIdNum)) {
              brokersQuery = brokersQuery.eq('id_scgp', scgpIdNum)
            }
          }
          if (filters?.brokerName) {
            brokersQuery = brokersQuery.ilike('name', `%${filters.brokerName}%`)
          }

          const { data: brokers, error: brokersError } = await brokersQuery

          if (!brokersError && brokers) {
            // Se há filtros por scgpId ou brokerName, filtrar os pedidos correspondentes
            const filteredBrokerIds = new Set(brokers.map((b: any) => b.id))
            const filteredShopBrokerIds = new Set(
              Array.from(shopBrokerToBrokerMap.entries())
                .filter(([_, brokerId]) => filteredBrokerIds.has(brokerId))
                .map(([shopBrokerId]) => shopBrokerId)
            )

            // Filtrar pedidos se necessário
            if (filters?.scgpId || filters?.brokerName) {
              orders = orders.filter(order => filteredShopBrokerIds.has(order.shop_broker_id))
            }

            const brokersMap = new Map<string, string>()
            brokers.forEach((b: any) => {
              brokersMap.set(b.id, b.name || '')
            })

            // Adicionar nomes dos brokers aos pedidos
            return orders.map(order => {
              const brokerId = shopBrokerToBrokerMap.get(order.shop_broker_id)
              return {
                ...order,
                broker: brokerId && brokersMap.has(brokerId)
                  ? { name: brokersMap.get(brokerId)! }
                  : null
              }
            })
          }
        }
      }
    }

    return orders.map(order => ({
      ...order,
      broker: null
    }))
  },

  async getOrder(id: string): Promise<ShopOrder | null> {
    const { data, error } = await supabase
      .from('shop_orders')
      .select(`
        *,
        items:shop_order_items(
          *,
          product:shop_products(*)
        )
      `)
      .eq('id', id)
      .single()

    if (error) {
      if (error.code === 'PGRST116') {
        return null
      }
      throw error
    }

    const order = data as ShopOrder

    // Buscar nome do broker através do relacionamento correto
    // shop_orders.shop_broker_id -> shop_brokers.id -> shop_brokers.broker_id -> brokers.id -> brokers.name
    if (order.shop_broker_id) {
      // Primeiro buscar shop_brokers para obter broker_id
      const { data: shopBroker, error: shopBrokerError } = await supabase
        .from('shop_brokers')
        .select('id, broker_id')
        .eq('id', order.shop_broker_id)
        .single()

      if (!shopBrokerError && shopBroker && shopBroker.broker_id) {
        // Agora buscar nome do broker
        const { data: broker, error: brokerError } = await supabase
          .from('brokers')
          .select('id, name')
          .eq('id', shopBroker.broker_id)
          .single()

        if (!brokerError && broker) {
          return {
            ...order,
            broker: { name: broker.name || '' }
          }
        }
      }
    }

    return {
      ...order,
      broker: null
    }
  },

  async getBrokerOrders(brokerId: string): Promise<ShopOrder[]> {
    // brokerId aqui é o broker_id (brokers.id), precisamos encontrar o shop_broker_id correspondente
    // Primeiro buscar shop_brokers pelo broker_id
    const { data: shopBroker, error: shopBrokerError } = await supabase
      .from('shop_brokers')
      .select('id')
      .eq('broker_id', brokerId)
      .single()

    if (shopBrokerError || !shopBroker) {
      return []
    }

    const shopBrokerId = shopBroker.id

    const { data, error } = await supabase
      .from('shop_orders')
      .select(`
        *,
        items:shop_order_items(
          *,
          product:shop_products(*)
        )
      `)
      .eq('shop_broker_id', shopBrokerId)
      .order('created_at', { ascending: false })

    if (error) throw error

    const orders = (data || []) as ShopOrder[]

    // Buscar nome do broker
    const { data: broker, error: brokerError } = await supabase
      .from('brokers')
      .select('id, name')
      .eq('id', brokerId)
      .single()

    if (!brokerError && broker) {
      const brokerName = broker.name || ''
      return orders.map(order => ({
        ...order,
        broker: { name: brokerName }
      }))
    }

    return orders.map(order => ({
      ...order,
      broker: null
    }))
  },

  async updateOrderStatus(id: string, updateData: ShopOrderUpdateData): Promise<ShopOrder | null> {
    const patch: ShopOrderUpdateData = { ...updateData }
    const now = new Date().toISOString()

    if (patch.status === 'shipped' || patch.status === 'delivered') {
      const { data: current, error: fetchError } = await supabase
        .from('shop_orders')
        .select('shipped_at, delivered_at')
        .eq('id', id)
        .single()

      if (!fetchError && current) {
        if (patch.status === 'shipped' && !current.shipped_at && patch.shipped_at === undefined) {
          patch.shipped_at = now
        }
        if (patch.status === 'delivered') {
          if (!current.delivered_at && patch.delivered_at === undefined) {
            patch.delivered_at = now
          }
          if (!current.shipped_at && patch.shipped_at === undefined) {
            patch.shipped_at = now
          }
        }
      }
    }

    const { data, error } = await supabase
      .from('shop_orders')
      .update(patch)
      .eq('id', id)
      .select('*')
      .single()

    if (error) throw error
    return data as ShopOrder
  },

  /**
   * Cancela um pedido: devolve estoque (com log), reverte pontos do corretor (com log) e atualiza status.
   * Registra quem cancelou e o motivo. Idempotente: se já cancelado, não executa devolução.
   */
  async cancelOrder(orderId: string, cancelledByUserId: string | null, cancellationReason: string): Promise<ShopOrder | null> {
    const order = await this.getOrder(orderId)
    if (!order) throw new Error('Pedido não encontrado.')
    if (order.status === 'cancelled') {
      return this.getOrder(orderId) as Promise<ShopOrder | null>
    }

    const reason = (cancellationReason || '').trim()
    const orderNumber = order.order_number || orderId
    const descriptionSuffix = reason ? ` Motivo: ${reason}` : ''

    // 1) Devolver estoque e registrar em shop_stock_movements
    if (order.items && order.items.length > 0) {
      for (const item of order.items) {
        const desc = `Devolução - cancelamento do pedido ${orderNumber}.${descriptionSuffix}`
        await this.registerStockMovement({
          product_id: item.product_id,
          movement_type: 'in',
          quantity: item.quantity,
          description: desc,
          created_by: cancelledByUserId ?? undefined,
          reference_type: 'order',
          reference_id: orderId,
        })
      }
    }

    // 2) Reverter pontuação do corretor e registrar em shop_points_history
    // shop_points_history.shop_broker_id referencia shop_brokers.id (shop_brokers.broker_id referencia brokers)
    if (order.shop_broker_id && order.total_points > 0) {
      const { data: shopBroker, error: brokerError } = await supabase
        .from('shop_brokers')
        .select('id, points_balance, total_points_spent')
        .eq('id', order.shop_broker_id)
        .single()

      if (brokerError || !shopBroker) throw new Error('Corretor do pedido não encontrado.')

      const currentBalance = (shopBroker as { points_balance?: number }).points_balance ?? 0
      const currentSpent = (shopBroker as { total_points_spent?: number }).total_points_spent ?? 0
      const newBalance = currentBalance + order.total_points
      const newSpent = Math.max(0, currentSpent - order.total_points)

      const { error: updateBrokerError } = await supabase
        .from('shop_brokers')
        .update({
          points_balance: newBalance,
          total_points_spent: newSpent,
          updated_at: new Date().toISOString(),
        })
        .eq('id', order.shop_broker_id)

      if (updateBrokerError) throw updateBrokerError

      const historyDesc = `Cancelamento pedido ${orderNumber}.${descriptionSuffix}`
      const { error: historyError } = await supabase
        .from('shop_points_history')
        .insert({
          shop_broker_id: order.shop_broker_id,
          transaction_type: 'refunded',
          points_amount: order.total_points,
          points_balance_before: currentBalance,
          points_balance_after: newBalance,
          reference_type: 'order',
          reference_id: orderId,
          description: historyDesc,
        })

      if (historyError) throw historyError
    }

    // 3) Atualizar status do pedido e registrar quem/motivo
    const updateData: ShopOrderUpdateData = {
      status: 'cancelled',
      cancelled_at: new Date().toISOString(),
      cancelled_by: cancelledByUserId ?? undefined,
      cancellation_reason: reason || undefined,
    }
    return this.updateOrderStatus(orderId, updateData)
  },

  // ============================================
  // HISTÓRICO DE PONTOS
  // ============================================
  async listPointsHistory(filters?: {
    brokerId?: string
    scgpId?: string
    brokerName?: string
    transactionType?: string
    dateFrom?: string
    dateTo?: string
  }): Promise<ShopPointsHistory[]> {
    let query = supabase
      .from('shop_points_history')
      .select('*')
      .order('created_at', { ascending: false })

    // Resolver shop_broker_ids por brokerId, scgpId ou brokerName
    let shopBrokerIdsFilter: string[] | undefined
    if (filters?.brokerId) {
      const { data: shopBroker, error: shopBrokerError } = await supabase
        .from('shop_brokers')
        .select('id')
        .eq('broker_id', filters.brokerId)
        .single()
      if (!shopBrokerError && shopBroker) {
        shopBrokerIdsFilter = [shopBroker.id]
      } else {
        return []
      }
    } else if (filters?.scgpId?.trim() || filters?.brokerName?.trim()) {
      const brokerIds = await resolveBrokerIdsInShopScope({
        scgpId: filters.scgpId,
        brokerName: filters.brokerName,
      })
      if (!brokerIds?.length) return []
      const { data: shopBrokers, error: sbError } = await supabase
        .from('shop_brokers')
        .select('id')
        .in('broker_id', brokerIds)
      if (sbError) throw sbError
      shopBrokerIdsFilter = (shopBrokers || []).map((sb: { id: string }) => sb.id)
      if (shopBrokerIdsFilter.length === 0) return []
    }

    if (shopBrokerIdsFilter !== undefined) {
      query = query.in('shop_broker_id', shopBrokerIdsFilter)
    }

    if (filters?.transactionType) {
      query = query.eq('transaction_type', filters.transactionType)
    }

    if (filters?.dateFrom) {
      query = query.gte('created_at', filters.dateFrom)
    }

    if (filters?.dateTo) {
      query = query.lte('created_at', filters.dateTo)
    }

    const { data, error } = await query

    if (error) {
      console.error('Erro ao buscar histórico de pontos:', error)
      throw error
    }

    const history = (data || []) as ShopPointsHistory[]

    // Buscar nomes dos brokers através do relacionamento correto
    // shop_points_history.shop_broker_id -> shop_brokers.id -> shop_brokers.broker_id -> brokers.id -> brokers.name
    const shopBrokerIds = Array.from(new Set(history.map(h => h.shop_broker_id).filter(Boolean)))
    const brokerNameByShopBrokerId = new Map<string, string>()
    if (shopBrokerIds.length > 0) {
      // Primeiro buscar shop_brokers para obter broker_id
      const { data: shopBrokers, error: shopBrokersError } = await supabase
        .from('shop_brokers')
        .select('id, broker_id')
        .in('id', shopBrokerIds)

      if (!shopBrokersError && shopBrokers) {
        const shopBrokerToBrokerMap = new Map<string, string>()
        shopBrokers.forEach((sb: any) => {
          shopBrokerToBrokerMap.set(sb.id, sb.broker_id)
        })

        // Agora buscar nomes dos brokers
        const brokerIds = Array.from(new Set(Array.from(shopBrokerToBrokerMap.values()).filter(Boolean)))
        if (brokerIds.length > 0) {
          const { data: brokers, error: brokersError } = await supabase
            .from('brokers')
            .select('id, name')
            .in('id', brokerIds)

          if (!brokersError && brokers) {
            const brokersMap = new Map<string, string>()
            brokers.forEach((b: any) => {
              brokersMap.set(b.id, b.name || '')
            })

            shopBrokerToBrokerMap.forEach((brokerId, shopBrokerId) => {
              if (brokersMap.has(brokerId)) {
                brokerNameByShopBrokerId.set(shopBrokerId, brokersMap.get(brokerId)!)
              }
            })
          }
        }
      }
    }

    // Nomes dos usuários que lançaram (created_by -> profiles)
    const createdByIds = Array.from(
      new Set(history.map((h) => h.created_by).filter((id): id is string => Boolean(id)))
    )
    const profilesMap = new Map<string, string>()
    if (createdByIds.length > 0) {
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('id, name')
        .in('id', createdByIds)

      if (!profilesError && profiles) {
        profiles.forEach((p: { id: string; name?: string | null }) => {
          profilesMap.set(p.id, p.name || '')
        })
      }
    }

    return history.map((item) => ({
      ...item,
      broker: brokerNameByShopBrokerId.has(item.shop_broker_id)
        ? { name: brokerNameByShopBrokerId.get(item.shop_broker_id)! }
        : null,
      created_by_user: item.created_by && profilesMap.has(item.created_by)
        ? { name: profilesMap.get(item.created_by)! }
        : null,
    }))
  },

  async getBrokerPointsHistory(brokerId: string): Promise<ShopPointsHistory[]> {
    return this.listPointsHistory({ brokerId })
  },

  // ============================================
  // ANALYTICS E INDICADORES
  // ============================================
  async getTopSellingProducts(limit: number = 10, dateFrom?: string, dateTo?: string): Promise<Array<{ product_id: string; product_name: string; total_quantity: number; total_points: number }>> {
    // Primeiro buscar pedidos filtrados por data
    let ordersQuery = supabase
      .from('shop_orders')
      .select('id, status, created_at')

    if (dateFrom) {
      ordersQuery = ordersQuery.gte('created_at', dateFrom)
    }
    if (dateTo) {
      ordersQuery = ordersQuery.lte('created_at', dateTo)
    }

    const { data: orders, error: ordersError } = await ordersQuery
    if (ordersError) throw ordersError

    const validOrderIds = orders?.filter((o: any) => o.status !== 'cancelled').map((o: any) => o.id) || []
    
    if (validOrderIds.length === 0) {
      return []
    }

    // Buscar itens dos pedidos válidos
    const { data: items, error: itemsError } = await supabase
      .from('shop_order_items')
      .select(`
        product_id,
        quantity,
        points_cost,
        product:shop_products(name)
      `)
      .in('order_id', validOrderIds)

    if (itemsError) throw itemsError

    // Agregar dados
    const productMap = new Map<string, { product_id: string; product_name: string; total_quantity: number; total_points: number }>()
    
    items?.forEach((item: any) => {
      if (!item.product_id) return
      
      const productName = item.product?.name || 'Produto Desconhecido'
      const quantity = item.quantity || 0
      const pointsCost = item.points_cost || 0
      const totalPoints = quantity * pointsCost

      if (productMap.has(item.product_id)) {
        const existing = productMap.get(item.product_id)!
        existing.total_quantity += quantity
        existing.total_points += totalPoints
      } else {
        productMap.set(item.product_id, {
          product_id: item.product_id,
          product_name: productName,
          total_quantity: quantity,
          total_points: totalPoints
        })
      }
    })

    return Array.from(productMap.values())
      .sort((a, b) => b.total_quantity - a.total_quantity)
      .slice(0, limit)
  },

  async getOrdersByStatus(dateFrom?: string, dateTo?: string): Promise<Array<{ status: string; count: number; total_points: number }>> {
    let query = supabase
      .from('shop_orders')
      .select('status, total_points, created_at')

    if (dateFrom) {
      query = query.gte('created_at', dateFrom)
    }
    if (dateTo) {
      query = query.lte('created_at', dateTo)
    }

    const { data, error } = await query

    if (error) throw error

    const statusMap = new Map<string, { status: string; count: number; total_points: number }>()

    data?.forEach((order: any) => {
      const status = order.status || 'unknown'
      const points = order.total_points || 0

      if (statusMap.has(status)) {
        const existing = statusMap.get(status)!
        existing.count += 1
        existing.total_points += points
      } else {
        statusMap.set(status, {
          status,
          count: 1,
          total_points: points
        })
      }
    })

    return Array.from(statusMap.values())
  },

  async getOrdersByMonth(
    months: number = 6,
    dateFrom?: string,
    dateTo?: string,
    scgpId?: string,
    brokerName?: string,
  ): Promise<Array<{ month: string; count: number; total_points: number }>> {
    const { startISO, endISO } = indicatorDateRange(months, dateFrom, dateTo)
    const brokerSet = await resolveShopBrokerIdsForIndicators(scgpId, brokerName)

    const { data, error } = await supabase
      .from('shop_orders')
      .select('created_at, total_points, status, shop_broker_id')
      .gte('created_at', startISO)
      .lte('created_at', endISO)

    if (error) throw error

    const monthMap = new Map<
      string,
      { monthKey: string; month: string; count: number; total_points: number }
    >()

    data?.forEach((order: any) => {
      if (order.status === 'cancelled') return
      if (brokerSet !== null) {
        if (!order.shop_broker_id || !brokerSet.has(order.shop_broker_id)) return
      }

      const date = new Date(order.created_at)
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
      const monthLabel = date.toLocaleDateString('pt-BR', { month: 'short', year: 'numeric' })
      const points = order.total_points || 0

      if (monthMap.has(monthKey)) {
        const existing = monthMap.get(monthKey)!
        existing.count += 1
        existing.total_points += points
      } else {
        monthMap.set(monthKey, {
          monthKey,
          month: monthLabel,
          count: 1,
          total_points: points,
        })
      }
    })

    return Array.from(monthMap.values())
      .sort((a, b) => a.monthKey.localeCompare(b.monthKey))
      .map(({ monthKey: _mk, ...row }) => row)
  },

  async getFreightTotalByMonth(
    months: number = 6,
    dateFrom?: string,
    dateTo?: string,
    scgpId?: string,
    brokerName?: string,
  ): Promise<Array<{ month: string; total_freight: number }>> {
    const { startISO, endISO } = indicatorDateRange(months, dateFrom, dateTo)
    const brokerSet = await resolveShopBrokerIdsForIndicators(scgpId, brokerName)

    const { data, error } = await supabase
      .from('shop_orders')
      .select('created_at, status, shipping_cost, shop_broker_id')
      .gte('created_at', startISO)
      .lte('created_at', endISO)

    if (error) throw error

    const monthMap = new Map<string, { monthKey: string; month: string; total_freight: number }>()

    data?.forEach((order: any) => {
      if (order.status === 'cancelled') return
      if (brokerSet !== null) {
        if (!order.shop_broker_id || !brokerSet.has(order.shop_broker_id)) return
      }

      const date = new Date(order.created_at)
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
      const monthLabel = date.toLocaleDateString('pt-BR', { month: 'short', year: 'numeric' })
      const cost = typeof order.shipping_cost === 'number' ? order.shipping_cost : parseFloat(order.shipping_cost)
      const add = Number.isFinite(cost) ? cost : 0

      if (monthMap.has(monthKey)) {
        monthMap.get(monthKey)!.total_freight += add
      } else {
        monthMap.set(monthKey, {
          monthKey,
          month: monthLabel,
          total_freight: add,
        })
      }
    })

    return Array.from(monthMap.values())
      .sort((a, b) => a.monthKey.localeCompare(b.monthKey))
      .map(({ monthKey: _mk, ...row }) => row)
  },

  async getAvgDeliveryLeadTimeByMonth(
    months: number = 6,
    dateFrom?: string,
    dateTo?: string,
    scgpId?: string,
    brokerName?: string,
  ): Promise<Array<{ month: string; avg_delivery_days: number }>> {
    const { startISO, endISO } = indicatorDateRange(months, dateFrom, dateTo)
    const brokerSet = await resolveShopBrokerIdsForIndicators(scgpId, brokerName)

    const startMs = new Date(startISO).getTime()
    const endMs = new Date(endISO).getTime()

    const { data, error } = await supabase
      .from('shop_orders')
      .select('created_at, delivered_at, updated_at, status, shop_broker_id')
      .eq('status', 'delivered')

    if (error) throw error

    const monthMap = new Map<
      string,
      { monthKey: string; month: string; sumDays: number; count: number }
    >()

    data?.forEach((order: any) => {
      const effectiveDeliveredAt = order.delivered_at || order.updated_at
      if (!effectiveDeliveredAt || !order.created_at) return
      if (brokerSet !== null) {
        if (!order.shop_broker_id || !brokerSet.has(order.shop_broker_id)) return
      }

      const deliveredMs = new Date(effectiveDeliveredAt).getTime()
      if (deliveredMs < startMs || deliveredMs > endMs) return

      const created = new Date(order.created_at).getTime()
      const days = (deliveredMs - created) / 86400000
      if (!Number.isFinite(days) || days < 0) return

      const d = new Date(effectiveDeliveredAt)
      const monthKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
      const monthLabel = d.toLocaleDateString('pt-BR', { month: 'short', year: 'numeric' })

      if (monthMap.has(monthKey)) {
        const agg = monthMap.get(monthKey)!
        agg.sumDays += days
        agg.count += 1
      } else {
        monthMap.set(monthKey, {
          monthKey,
          month: monthLabel,
          sumDays: days,
          count: 1,
        })
      }
    })

    return Array.from(monthMap.values())
      .sort((a, b) => a.monthKey.localeCompare(b.monthKey))
      .map(({ monthKey: _mk, sumDays, count, month }) => ({
        month,
        avg_delivery_days: Math.round((sumDays / count) * 10) / 10,
      }))
  },

  async getTopBrokers(limit: number = 10, dateFrom?: string, dateTo?: string, scgpId?: string, brokerName?: string): Promise<Array<{ broker_id: string; broker_name?: string; total_orders: number; total_points: number }>> {
    let query = supabase
      .from('shop_orders')
      .select('shop_broker_id, total_points, created_at, status')

    if (dateFrom) {
      query = query.gte('created_at', dateFrom)
    }
    if (dateTo) {
      query = query.lte('created_at', dateTo)
    }

    const { data, error } = await query

    if (error) throw error

    // Agregar por shop_broker_id primeiro
    const shopBrokerMap = new Map<string, { shop_broker_id: string; total_orders: number; total_points: number }>()

    data?.forEach((order: any) => {
      if (order.status === 'cancelled' || !order.shop_broker_id) return
      
      const points = order.total_points || 0

      if (shopBrokerMap.has(order.shop_broker_id)) {
        const existing = shopBrokerMap.get(order.shop_broker_id)!
        existing.total_orders += 1
        existing.total_points += points
      } else {
        shopBrokerMap.set(order.shop_broker_id, {
          shop_broker_id: order.shop_broker_id,
          total_orders: 1,
          total_points: points
        })
      }
    })

    const topShopBrokers = Array.from(shopBrokerMap.values())
      .sort((a, b) => b.total_points - a.total_points)
      .slice(0, limit)

    // Buscar broker_id através de shop_brokers
    const shopBrokerIds = topShopBrokers.map(b => b.shop_broker_id)
    if (shopBrokerIds.length > 0) {
      const { data: shopBrokers, error: shopBrokersError } = await supabase
        .from('shop_brokers')
        .select('id, broker_id')
        .in('id', shopBrokerIds)

      if (!shopBrokersError && shopBrokers) {
        const shopBrokerToBrokerMap = new Map<string, string>()
        shopBrokers.forEach((sb: any) => {
          shopBrokerToBrokerMap.set(sb.id, sb.broker_id)
        })

        // Agora buscar nomes dos brokers
        const brokerIds = Array.from(new Set(Array.from(shopBrokerToBrokerMap.values()).filter(Boolean)))
        if (brokerIds.length > 0) {
          let brokersQuery = supabase
            .from('brokers')
            .select('id, name')
            .in('id', brokerIds)

          // Aplicar filtros por ID SCGP e nome do corretor
          if (scgpId && scgpId.trim() !== '') {
            const scgpIdNum = parseInt(scgpId)
            if (!isNaN(scgpIdNum)) {
              brokersQuery = brokersQuery.eq('id_scgp', scgpIdNum)
            }
          }
          if (brokerName) {
            brokersQuery = brokersQuery.ilike('name', `%${brokerName}%`)
          }

          const { data: brokers, error: brokersError } = await brokersQuery

          if (!brokersError && brokers) {
            // Se há filtros, filtrar os resultados correspondentes
            const filteredBrokerIds = new Set(brokers.map((b: any) => b.id))
            const filteredShopBrokerIds = new Set(
              Array.from(shopBrokerToBrokerMap.entries())
                .filter(([_, brokerId]) => filteredBrokerIds.has(brokerId))
                .map(([shopBrokerId]) => shopBrokerId)
            )

            // Filtrar topShopBrokers se necessário
            let filteredTopShopBrokers = topShopBrokers
            if (scgpId || brokerName) {
              filteredTopShopBrokers = topShopBrokers.filter(
                sb => filteredShopBrokerIds.has(sb.shop_broker_id)
              )
            }

            const brokersMap = new Map<string, string>()
            brokers.forEach((b: any) => {
              brokersMap.set(b.id, b.name || '')
            })

            // Mapear resultados finais
            return filteredTopShopBrokers.map(shopBroker => {
              const brokerId = shopBrokerToBrokerMap.get(shopBroker.shop_broker_id)
              return {
                broker_id: brokerId || '',
                broker_name: brokerId && brokersMap.has(brokerId) ? brokersMap.get(brokerId) : undefined,
                total_orders: shopBroker.total_orders,
                total_points: shopBroker.total_points
              }
            })
          }
        }
      }
    }

    return topShopBrokers.map(shopBroker => ({
      broker_id: '',
      broker_name: undefined,
      total_orders: shopBroker.total_orders,
      total_points: shopBroker.total_points
    }))
  },

  async getShopMetrics(dateFrom?: string, dateTo?: string): Promise<{
    total_orders: number
    total_points_disponibilizados: number
    total_points_spent: number
    total_products_sold: number
    average_order_value: number
    total_brokers: number
  }> {
    let ordersQuery = supabase
      .from('shop_orders')
      .select('id, shop_broker_id, total_points, status, created_at')

    if (dateFrom) {
      ordersQuery = ordersQuery.gte('created_at', dateFrom)
    }
    if (dateTo) {
      ordersQuery = ordersQuery.lte('created_at', dateTo)
    }

    const { data: orders, error: ordersError } = await ordersQuery

    if (ordersError) throw ordersError

    const validOrders = orders?.filter((o: any) => o.status !== 'cancelled') || []
    const total_orders = validOrders.length
    const total_points_spent = validOrders.reduce((sum: number, o: any) => sum + (o.total_points || 0), 0)
    const average_order_value = total_orders > 0 ? total_points_spent / total_orders : 0

    // Contar produtos vendidos
    const validOrderIds = validOrders.map((o: any) => o.id)
    let itemsQuery = supabase
      .from('shop_order_items')
      .select('quantity')

    if (validOrderIds.length > 0) {
      itemsQuery = itemsQuery.in('order_id', validOrderIds)
    } else {
      itemsQuery = itemsQuery.eq('order_id', '00000000-0000-0000-0000-000000000000') // Retornar vazio
    }

    const { data: items, error: itemsError } = await itemsQuery

    if (itemsError) throw itemsError

    const total_products_sold = items?.reduce((sum: number, item: any) => sum + (item.quantity || 0), 0) || 0

    // Contar corretores únicos através de shop_broker_id
    const uniqueShopBrokers = new Set(validOrders.map((o: any) => o.shop_broker_id).filter(Boolean))
    const total_brokers = uniqueShopBrokers.size

    let pointsHistoryQuery = supabase
      .from('shop_points_history')
      .select('points_amount')
      .gt('points_amount', 0)

    if (dateFrom) {
      pointsHistoryQuery = pointsHistoryQuery.gte('created_at', dateFrom)
    }
    if (dateTo) {
      pointsHistoryQuery = pointsHistoryQuery.lte('created_at', dateTo)
    }

    const { data: pointsHistory, error: pointsHistoryError } = await pointsHistoryQuery

    if (pointsHistoryError) throw pointsHistoryError

    const total_points_disponibilizados =
      pointsHistory?.reduce((sum: number, entry: { points_amount?: number }) => sum + (entry.points_amount || 0), 0) || 0

    return {
      total_orders,
      total_points_disponibilizados,
      total_points_spent,
      total_products_sold,
      average_order_value,
      total_brokers
    }
  },

  async getTopFavoriteProducts(limit: number = 10): Promise<Array<{ product_id: string; product_name: string; favorite_count: number }>> {
    const { data, error } = await supabase
      .from('shop_favorite_products')
      .select(`
        product_id,
        product:shop_products(name)
      `)

    if (error) throw error

    // Agregar contagem de favoritos por produto
    const productMap = new Map<string, { product_id: string; product_name: string; favorite_count: number }>()
    
    data?.forEach((item: any) => {
      if (!item.product_id) return
      
      const productName = item.product?.name || 'Produto Desconhecido'

      if (productMap.has(item.product_id)) {
        const existing = productMap.get(item.product_id)!
        existing.favorite_count += 1
      } else {
        productMap.set(item.product_id, {
          product_id: item.product_id,
          product_name: productName,
          favorite_count: 1
        })
      }
    })

    return Array.from(productMap.values())
      .sort((a, b) => b.favorite_count - a.favorite_count)
      .slice(0, limit)
  },

  // ============================================
  // FUNÇÕES AUXILIARES
  // ============================================
  /**
   * Ajuste manual de PASI Points de um corretor (shop_brokers.id).
   * points_delta: positivo = crédito; negativo = débito.
   * Atualiza saldo, total_points_earned (só no crédito) e grava extrato com created_by.
   */
  async adjustBrokerPoints(params: {
    shopBrokerId: string
    pointsDelta: number
    description: string
    createdBy?: string | null
  }): Promise<ShopPointsHistory> {
    const { shopBrokerId, pointsDelta, description, createdBy = null } = params
    const obs = (description || '').trim()
    if (!obs) throw new Error('Observação é obrigatória.')
    if (!Number.isFinite(pointsDelta) || pointsDelta === 0) {
      throw new Error('Informe uma quantidade diferente de zero.')
    }
    if (!Number.isInteger(pointsDelta)) {
      throw new Error('A quantidade deve ser um número inteiro.')
    }

    const { data: shopBroker, error: brokerError } = await supabase
      .from('shop_brokers')
      .select('id, points_balance, total_points_earned')
      .eq('id', shopBrokerId)
      .single()

    if (brokerError || !shopBroker) throw new Error('Corretor não encontrado no Shop.')

    const currentBalance = (shopBroker as { points_balance?: number }).points_balance ?? 0
    const currentEarned = (shopBroker as { total_points_earned?: number }).total_points_earned ?? 0
    const newBalance = currentBalance + pointsDelta
    if (newBalance < 0) {
      throw new Error(
        `Saldo insuficiente. Disponível: ${currentBalance.toLocaleString('pt-BR')} PASI Points.`
      )
    }
    const patch: {
      points_balance: number
      updated_at: string
      total_points_earned?: number
    } = {
      points_balance: newBalance,
      updated_at: new Date().toISOString(),
    }
    if (pointsDelta > 0) {
      patch.total_points_earned = currentEarned + pointsDelta
    }

    const { error: updateError } = await supabase
      .from('shop_brokers')
      .update(patch)
      .eq('id', shopBrokerId)

    if (updateError) throw updateError

    const { data: historyRow, error: historyError } = await supabase
      .from('shop_points_history')
      .insert({
        shop_broker_id: shopBrokerId,
        transaction_type: pointsDelta > 0 ? 'earned' : 'adjusted',
        points_amount: pointsDelta,
        points_balance_before: currentBalance,
        points_balance_after: newBalance,
        reference_type: 'manual',
        description: obs,
        created_by: createdBy || null,
      })
      .select('*')
      .single()

    if (historyError) throw historyError
    return historyRow as ShopPointsHistory
  },
}

